import './App.css';
import { Link } from "react-router-dom";
import NetworkGraph from "./components/NetworkGraph";
import { IconGear } from "./components/Icons";

/* =========================================================================
   AssistenciaTecnicaPlans
   Página de planos de Assistência Técnica (ST 1 a ST 4).

   Diferente do HelpDeskPlans, aqui existe uma única categoria de planos
   (não há alternância "Por Usuário / Por Computador") e não há cobrança
   anual — apenas o valor mensal fixo de cada plano.

   Cada card lista somente os recursos marcados com check para aquele
   plano, agrupados pelas mesmas categorias da tabela de referência
   (Pacote de instalação básico, Pacote de uso básico, Pacote de uso
   avançado, Backup e Transporte).

   Uso: <AssistenciaTecnicaPlans /> — componente independente, sem
   dependência de outras páginas. Ajuste o "CTA_HREF" para o link correto
   do formulário/contato, e "SUPORTE_HREF" para a página de tipos de
   suporte, caso o caminho mude.
   ========================================================================= */

const CTA_HREF = '/contato';
const SUPORTE_HREF = '/servicos/suporte/#suporte';

const TIERS = [
  {
    id: 'st1',
    label: 'ST 1',
    name: 'Usuário Doméstico',
    tag: 'Suporte técnico essencial para uso pessoal',
    mensal: 120,
  },
  {
    id: 'st2',
    label: 'ST 2',
    name: 'Usuário Home Office',
    tag: 'Para quem trabalha de casa e precisa de mais estrutura',
    mensal: 160,
  },
  {
    id: 'st3',
    label: 'ST 3',
    name: 'Usuário Empresa Básico',
    tag: 'O mais escolhido pelas pequenas empresas',
    featured: true,
    mensal: 250,
  },
  {
    id: 'st4',
    label: 'ST 4',
    name: 'Usuário Empresa Avançado',
    tag: 'Gestão completa para empresas com maior demanda',
    mensal: 320,
  },
];

/* Recursos agrupados pelas mesmas categorias da tabela de referência.
   A ordem dos itens dentro de cada grupo é a mesma usada em FEATURE_MATRIX. */
const FEATURE_GROUPS = [
  {
    title: 'Pacote de instalação básico',
    items: [
      'Configuração de BIOS/BOOT',
      'Instalação do Sistema Operacional (Versão DEMO)',
      'Atualização do Sistema Operacional',
      'Instalação e Atualização de Drivers',
    ],
  },
  {
    title: 'Pacote de uso básico',
    items: [
      'Pacote Office - Outlook/Word/Excel/PowerPoint (Versão TRIAL)',
      'Máquina Virtual Java (JVM)',
      'Pacote FrameWork',
      'Browser - Chrome/Firefox/Edge (Nativo)',
      'Leitor PDF - FoxIt',
      'Extrator de arquivos - WinRAR (Versão Free)',
      'Serviço Remoto - AnyDesk/TeamViewer/RustDesk',
      'Limpeza de Disco - CleanUp!/Limpeza de Disco (Nativo)',
    ],
  },
  {
    title: 'Pacote de uso avançado',
    items: [
      'Photoshop',
      'AutoCAD',
      'SketchUp',
      'PROMOB',
      'LUMIER',
    ],
  },
  {
    title: 'Backup',
    items: [
      'Backup Básico (Documentos, Área de Trabalho, Imagens, Vídeos) — 50GB',
      'Backup Completo (Cópia do Disco)',
      'Backup Completo II (Cópia do Disco/Outlook.pst/Favoritos Chrome/Firefox/Edge)',
      'Backup Personalizado (além do Backup Completo II, sob demanda do cliente)',
    ],
  },
  {
    title: 'Transporte *',
    items: [
      'UberFlash',
      'MotoBoy',
      'Entrega com Técnico',
    ],
  },
];

/* 1 = incluso, 0 = não incluso. Mesma ordem dos itens em FEATURE_GROUPS,
   lida em sequência (instalação básico → uso básico → uso avançado →
   backup → transporte). */
const FEATURE_MATRIX = {
  st1: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  st2: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0],
  st3: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0, 0, 0],
  st4: [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
};

function formatPrice(value) {
  return value.toFixed(2).replace('.', ',');
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 10.5L8 14.5L16 5.5"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* Retorna, para um tier, os grupos de recursos já filtrados — apenas os
   itens marcados com check aparecem, e um grupo some por completo se
   nenhum dos seus itens estiver incluso naquele plano. */
function getIncludedGroups(tierId) {
  const flags = FEATURE_MATRIX[tierId];
  let cursor = 0;

  return FEATURE_GROUPS.map((group) => {
    const items = group.items
      .map((label, i) => ({ label, included: flags[cursor + i] === 1 }))
      .filter((item) => item.included);
    cursor += group.items.length;
    return { title: group.title, items };
  }).filter((group) => group.items.length > 0);
}

export default function AssistenciaTecnicaPlans() {
  return (
    <>
      <section className="page-hero">
        <div className="page-hero__bg"></div>
        <div className="page-hero__grid"></div>
        <NetworkGraph variant="hero" className="page-hero__network" />
        <div className="container">
          <div className="tag animate-up animate-up--d1">
            <IconGear style={{ width: "13px", height: "13px" }} />
            ASSISTÊNCIA TÉCNICA · SUPORTE ESPECIALIZADO
          </div>
          <h1
            className="animate-up animate-up--d2"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(36px,5vw,64px)",
              fontWeight: 700,
              letterSpacing: "-.03em",
              lineHeight: "1.1",
              marginBottom: "24px",
              color: "var(--text-primary)",
            }}
          >
            Assistência Técnica
            <br />
            <span className="text-blue">
              Seu computador sempre pronto para o trabalho
            </span>
          </h1>
          <p
            className="animate-up animate-up--d3"
            style={{
              fontSize: "18px",
              color: "var(--text-secondary)",
              maxWidth: "560px",
              lineHeight: "1.7",
              marginBottom: "40px",
            }}
          >
            Da instalação do sistema operacional à limpeza de disco, backup e softwares essenciais — a sepolTECH cuida da manutenção completa do seu equipamento, com planos mensais que se ajustam ao seu perfil de uso.
          </p>
          <div className="hero-actions-col animate-up animate-up--d4">
            <Link to={CTA_HREF} className="btn btn-primary">
              Solicitar orçamento →
            </Link>
            <Link to={SUPORTE_HREF} className="btn btn-outline">
              Ver tipos de suporte
            </Link>
          </div>
        </div>
      </section>

      <section className="hd-section" id="planos-assistencia-tecnica">
        <div className="hd-header">
          <h2>Planos de Assistência Técnica</h2>
          <p>
            Manutenção e configuração completa do seu equipamento, com plano fixo mensal.{' '}
            <Link to={SUPORTE_HREF} style={{ color: 'var(--hd-blue-mid)', fontWeight: 600 }}>
              Conheça os tipos de suporte →
            </Link>
          </p>
        </div>

        <div className="hd-grid">
          {TIERS.map((tier) => {
            const groups = getIncludedGroups(tier.id);

            return (
              <div
                key={tier.id}
                className={`hd-card ${tier.featured ? 'hd-card-featured' : ''}`}
              >
                {tier.featured && <span className="hd-featured-tag">Mais Popular</span>}

                <span className="hd-tier-badge">{tier.label}</span>
                <h3 className="hd-name">{tier.name}</h3>
                <p className="hd-tag">{tier.tag}</p>

                <div className="hd-price">
                  <sup>R$</sup>
                  <span className="hd-price-value">{formatPrice(tier.mensal)}</span>
                  <sub>/mês</sub>
                </div>

                <p className="hd-period">Cobrança mensal</p>

                <div className="hd-divider" />

                <div className="hd-features">
                  {groups.map((group) => (
                    <div key={group.title} style={{ marginBottom: '14px' }}>
                      <p
                        style={{
                          fontFamily: 'var(--font-mono)',
                          fontSize: '10.5px',
                          fontWeight: 700,
                          letterSpacing: '.06em',
                          textTransform: 'uppercase',
                          color: 'var(--hd-muted-soft)',
                          margin: '0 0 4px',
                        }}
                      >
                        {group.title}
                      </p>
                      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                        {group.items.map((item) => (
                          <li key={item.label} className="hd-feature-on">
                            <span className="hd-check">
                              <CheckIcon />
                            </span>
                            {item.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <a
                  href={CTA_HREF}
                  className={`hd-cta ${tier.featured ? 'hd-cta-filled' : 'hd-cta-outline'}`}
                >
                  Contratar {tier.label}
                </a>
              </div>
            );
          })}
        </div>

        <div className="hd-note-box">
          <p>
            <strong>Diagnóstico técnico:</strong> será cobrado R$ 90,00, isento para orçamento aprovado. Orçamento sem diagnóstico técnico terá o diagnóstico cobrado caso o equipamento não seja
            compatível durante o processo de trabalho.
          </p>
        </div>

        <div className="hd-note-box">
          <p><strong>Backup:</strong> não será realizado backup em máquinas com BitLocker ativado.</p>
          <p>Backup acima de 300GB terá cobrança adicional no valor de R$ 0,19 por GB excedente.</p>
          <p>
            Os backups serão armazenados em nossos servidores por no prazo máximo de 90 dias — caso
            queira manter o backup por período maior, consulte os valores.
          </p>
        </div>

        <div className="hd-note-box">
          <p>
            <strong>* Transporte:</strong> disponível apenas no ST 4, consulte o raio de atuação.
          </p>
          <p>Se o local da entrega não disponibilizar estacionamento gratuito, o mesmo será cobrado do cliente.</p>
        </div>
      </section>
    </>
  );
}
